import { ClientDetails } from '@clients/models/client-details';
import { ClientParent } from '@client-parents/models/client-parent';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl } from '@angular/forms';
import { Limiter } from '@common/enums/limiter';
import { MojoWellKnownNames } from '@common/constants/mojo-well-known-names';
import { of, empty } from 'rxjs';
import { RoleService } from '@roles/services/role.service';
import { select, Store } from '@ngrx/store';
import { SelectItem } from 'primeng/api';
import { takeWhile, catchError, map } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { User } from '@users/models/user';
import { UserLimiter } from '@users/models/user-limiter';
import { UserService } from '@users/services/user.service';
import { UserSettings } from '@user-settings/models/user-settings';
import * as userSettingsSelectors from '@user-settings/selectors/user-settings';
import * as _lodash from 'lodash';

@Component({
  selector: 'mojo-user-editor',
  templateUrl: './user-editor.component.html'
})
export class UserEditorComponent implements OnInit, OnDestroy {

  @Input()
  dialogTitle = 'User Details';
  @Output()
  onOpen = new EventEmitter();
  @Output()
  onCancel = new EventEmitter();
  @Output()
  onSaveComplete = new EventEmitter<any>();
  @Output()
  onDisableUser = new EventEmitter<User>();
  @Input()
  limitedByOptions: UserLimiter[];
  @Input()
  clients: ClientDetails[];
  @Input()
  clientParents: ClientParent[];
  isAlive: boolean = true;
  private user: User;
  private original: User;
  private cloneUser = (user: User = null) => _lodash.cloneDeep(user || this.user);
  private userSettings: UserSettings;
  isNewUser: boolean;
  allowActions: boolean = true;
  availableRoles: SelectItem[];
  form: UntypedFormGroup;
  displayDialog: boolean;
  currentUserIsSystemAdmin: boolean;
  currentUserIsClientAdmin: boolean;

  constructor(
    private readonly store: Store<any>,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly roleService: RoleService,
    private readonly userService: UserService,
    private readonly messageService: ToastrService) {

    this.store.pipe(
      select(userSettingsSelectors.getUserSettings),
      takeWhile(() => this.isAlive)
    ).subscribe((userSettings) => {
      this.userSettings = userSettings;
    });

    this.configureRoles();
    this.buildForm();
  }

  ngOnInit() { }

  ngOnDestroy() {
    this.isAlive = false;
  }

  disable(): void {
    this.onDisableUser.emit(this.cloneUser());
  }


  save(): void {
    const updatedUser = this.mapFormToUser();
    this.allowActions = false;
    if (this.isNewUser) {
      this.userService.createUser(updatedUser).pipe(
        map(( result ) => {

          if (!result.success) {
            this.messageService.error(`User '${updatedUser.email} not saved`, 'Save Error');
            this.allowActions = true;
            return;
          }

          this.displayDialog = false;
          this.allowActions = true;
          this.messageService.success(`New User '${updatedUser.email} created`, 'New User Created');
          this.onSaveComplete.emit(null);
        }),
        catchError(() => {
          this.messageService.error(`User '${updatedUser.email} not saved`, 'Save Error');
          this.allowActions = true;
          return empty();
        }),
        takeWhile(() => this.isAlive)
      ).subscribe();
    } else {
      this.userService.updateUser(updatedUser).pipe(
        catchError(() => {
          this.messageService.error(`User '${updatedUser.email} not saved`, 'Save Error');
          this.allowActions = true;
          return of([]);
        }),
        takeWhile(() => this.isAlive)
      ).subscribe(() => {
        this.displayDialog = false;
        this.allowActions = true;
        this.messageService.success(`User '${updatedUser.email} Updated`, 'User Updated');
        this.onSaveComplete.emit(null);
      });
    }
  }

  get limitedAccessRequired(): boolean {
    const hasRole = this.role.value;
    const selectedRoleRequiresLimitedAccess = (
      this.role.value !== MojoWellKnownNames.RoleIds.SystemAdministrator
    );
    const result = (
      hasRole && (
        selectedRoleRequiresLimitedAccess || (this.currentUserIsClientAdmin && this.userSettings.ClientRef == null)
      )
    );
    return result;
  }

  cancel(evt: Event = null): void {
    this.reset();
    this.displayDialog = false;
    this.onCancel.emit();
  }

  reset(): void {
    this.user = this.cloneUser(this.original);
    this.form.setValue(this.mapUserToForm(this.user));
  }


  private mapUserToForm(user: User) {
    const clientParent = user.clientParentRef ? this.clientParents.find(i => i.Id === user.clientParentRef) : null;
    const client = user.clientRef ? this.clients.find(i => i.Ref === user.clientRef) : null;
    return {
      email: user.email,
      role: user.roleId,
      limitedBy: user.limitedBy || new UserLimiter(),
      selectedClient: client ? client : null,
      selectedClientParent: clientParent ? clientParent : null
    };
  }

  private mapFormToUser(): User {
    const user = this.cloneUser(this.user);

    user.email = this.email.value;
    user.roleId = this.role.value;
    user.role = this.availableRoles.filter(x => x.value == this.role.value)[0].label;
    user.limitedBy = this.limitedBy.value;

    if (user.limitedBy.limiter === Limiter.ClientRef && this.selectedClient.value) {
      user.clientRef = this.selectedClient.value.Ref;
      user.clientParentRef = null;
    } else if (user.limitedBy.limiter === Limiter.ClientParentRef && this.selectedClientParent.value) {
      user.clientParentRef = this.selectedClientParent.value.Id;
      user.clientRef = null;
    } else {
      user.clientRef = null;
      user.clientParentRef = null;
    }
    return user;
  }

  showDialog(user: User, createNew: boolean): void {
    this.isNewUser = createNew;
    this.displayDialog = true;
    this.onOpen.emit();
    this.original = this.cloneUser(user);
    this.user = this.cloneUser(this.original);
    this.buildForm();
    this.form.setValue(this.mapUserToForm(user));
  }


  private makeSelectItem(label: string, value: any) {
    return { label: label, value: value };
  }

  private buildForm(): void {

    const emailMaxlength = 254;

    this.form = this.formBuilder.group(
      {
        email: [
          '',
          [
            Validators.required,
            Validators.email,
            Validators.maxLength(emailMaxlength)
          ]
        ],
        role: ['', [Validators.required]],
        limitedBy: [''],
        selectedClient: ['', [this.getSelectedClientValidator()]],
        selectedClientParent: ['', [this.getSelectedClientParentValidator()]],
      },
    );

    this.limitedBy.valueChanges.pipe(
      takeWhile(() => this.isAlive)
    ).subscribe(
      () => {
        this.selectedClient.setValue(null);
        this.selectedClientParent.setValue(null);
      });

    this.role.valueChanges
      .subscribe(
        (role: string) => {
          if (role === MojoWellKnownNames.RoleIds.SystemAdministrator ) {
            this.limitedBy.clearValidators();
            this.limitedBy.updateValueAndValidity();
          } else if (this.limitedAccessRequired) {
            this.limitedBy.setValidators(this.limitedByValidator);
          }
        });
  }

  getSelectedClientValidator() {
    return (c: AbstractControl) => {

      if (!this.selectedRoleRequiresClientInfo() || !this.limitedAccessRequired) {
        return null;
      }

      const limitedBy = this.limitedBy ? this.limitedBy.value : null;
      if (!limitedBy) {
        return null;
      }

      return (limitedBy.limiter === Limiter.ClientParentRef || (limitedBy.limiter === Limiter.ClientRef && c.value) ? null : { required: true });

    };
  }

  private selectedRoleRequiresClientInfo(): boolean {
    const role = this.role ? this.role.value : null;
    return role === MojoWellKnownNames.RoleIds.ClientAdministrator || role === MojoWellKnownNames.RoleIds.User || role === MojoWellKnownNames.RoleIds.ClientSeniorUser;
  }

  getSelectedClientParentValidator() {
    return (c: AbstractControl) => {

      if (!this.selectedRoleRequiresClientInfo() || !this.limitedAccessRequired) {
        return null;
      }

      const limitedBy = this.limitedBy ? this.limitedBy.value : null;
      if (!limitedBy) {
        return null;
      }

      return (limitedBy.limiter === Limiter.ClientRef || (limitedBy.limiter === Limiter.ClientParentRef && c.value) ? null : { required: true });

    };
  }

  limitedByValidator(control: AbstractControl) {
    const limiter: UserLimiter = control.value;
    return (limiter.limiter !== Limiter.ClientParentRef && limiter.limiter !== Limiter.ClientRef)
      ? { required: true }
      : null;
  }

  get email(): AbstractControl {
    return this.form ? this.form.controls['email'] : null;
  }

  get role(): AbstractControl {
    return this.form ? this.form.controls['role'] : null;
  }

  get selectedClient(): AbstractControl {
    return this.form ? this.form.controls['selectedClient'] : null;
  }

  get selectedClientParent(): AbstractControl {
    return this.form ? this.form.controls['selectedClientParent'] : null;
  }

  get limitedBy(): AbstractControl {
    return this.form ? this.form.controls['limitedBy'] : null;
  }

  get isLimitedByClientRef(): boolean {
    const limiter: UserLimiter = this.limitedBy.value;
    return limiter ? limiter.limiter === Limiter.ClientRef : false;
  }

  get isLimitedByClientParentRef(): boolean {
    const limiter: UserLimiter = this.limitedBy.value;
    return limiter ? limiter.limiter === Limiter.ClientParentRef : false;
  }

  private configureRoles(): void {
      this.availableRoles = [];
    this.availableRoles.push(this.makeSelectItem(MojoWellKnownNames.RoleNames.ClientAdministrator, MojoWellKnownNames.RoleIds.ClientAdministrator));
    this.availableRoles.push(this.makeSelectItem(MojoWellKnownNames.RoleNames.ClientSeniorUser, MojoWellKnownNames.RoleIds.ClientSeniorUser));
    this.availableRoles.push(this.makeSelectItem(MojoWellKnownNames.RoleNames.User, MojoWellKnownNames.RoleIds.User));
  }

}
